<template>
  <div ref="providerRoot" @focusout="onFocusOut">
    <slot v-bind="validationProps" />
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { computed, ref, nextTick, watch } from 'vue';
import { isEqual } from 'lodash';
import { parseRules } from '/@/modules/core/form/vuelidateValidations';

export default {
  name: 'ValidationProvider',
  props: {
    rules: {
      type: [Object, String, Function],
      default: null
    },
    name: {
      type: String,
      required: false,
      default: 'field'
    },
    value: {
      type: [String, Number, Boolean, Object, Array, Date],
      default: ''
    }
  },
  setup(props) {
    // Local state for field value
    const fieldValue = ref(props.value);
    const hasValidated = ref(false);
    const internalRules = ref(props.rules);
    const isDirty = ref(false);
    const isInitialMount = ref(true);

    // Watch for deep changes in rules
    watch(
      () => props.rules,
      (newRules) => {
        if (!isEqual(newRules, internalRules.value)) {
          internalRules.value = newRules;
        }
      },
      { deep: true }
    );

    // Watch for value prop changes (external updates)
    watch(
      () => props.value,
      (newValue) => {
        if (newValue !== fieldValue.value) {
          fieldValue.value = newValue;
        }
      }
    );

    // Parse validation rules
    const validationRules = computed(() => {
      const rules = internalRules.value ? parseRules(internalRules.value) : {};
      return {
        [props.name]: rules
      };
    });

    // Create Vuelidate instance
    const v$ = useVuelidate(validationRules, { [props.name]: fieldValue });

    // Watch internal value changes (v-model updates)
    watch(fieldValue, () => {
      // Skip $touch() on initial mount to prevent showing errors immediately
      if (isInitialMount.value) {
        isInitialMount.value = false;
        return;
      }
      nextTick(() => {
        v$.value.$touch();
      });
    });

    // Methods
    const onFocusOut = () => {
      v$.value.$touch();
    };

    const validate = async () => {
      hasValidated.value = true;
      isDirty.value = true;
      await v$.value.$touch();
      await v$.value.$validate();
      return !v$.value[props.name].$invalid;
    };

    const reset = () => {
      hasValidated.value = false;
      isDirty.value = false;
      isInitialMount.value = true;
      v$.value.$reset();
    };

    // Computed Props Construction
    const validationProps = computed(() => {
      const fieldV$ = v$.value[props.name];
      const errors = fieldV$?.$errors?.map((e) => e.$message) || [];

      return {
        // Vuelidate native
        v$: fieldV$,

        // Common properties
        errors,
        validate,
        value: fieldValue.value,
        dirty: fieldV$?.$dirty || isDirty.value,
        touched: fieldV$?.$dirty || isDirty.value,
        untouched: !(fieldV$?.$dirty || isDirty.value),

        // Common aliases
        reset
      };
    });

    return {
      validationProps,
      validate,
      reset,
      onFocusOut
    };
  }
};
</script>
